import type { Animation, BookTexture, SettingsValues, Viewer } from "@pdfking/book3d"
import { Separator } from "@pdfking/components"
import type { DestinationID, ImposeResult } from "@pdfking/imposition"
import { BuildPrintLayers } from "@pdfking/imposition"
import { AddMargins, Max, Min, ToSpatial } from "@pdfking/math"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { PageNumber, PrintLayer, RequestRaster } from "@pdfking/renderer-api"
import { MatrixBox } from "@pdfking/renderer-api"
import type { FileName } from "@pdfking/structs"
import type { StringGUID } from "@pdfking/tfunc"
import { Items, JSONEncode, Keys } from "@pdfking/tfunc"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import type { ComponentChildren } from "preact"

import type { Guide, RequestMedia, SlotMedia } from "#public"

import { useTranslationTyped } from "../i18n/useTranslation"
import { RootMixin } from "../mixins/RootMixin"
import { GuideToggles } from "./GuideToggles"
import { Pager } from "./Pager"
import { StepButton } from "./StepButton"
import { ViewerHeader } from "./ViewerHeader"
import { ZoomControl } from "./ZoomControl"

// Keeps three.js (and the rest of book3d) out of the main chunk — it only
// loads the first time the 3D view is opened.
const create_book_viewer = (
  container: HTMLElement,
  settings: Partial<SettingsValues>,
  request_raster: RequestRaster
) => import("@pdfking/book3d").then(m => m.CreateBookViewer(container, settings, request_raster))

export type CameraMode = "free" | "front"

// One split upload page on its absolute book page, through its file matrix.
// Named by the upload page it shows: the book caches textures by that name, so
// a slot filled by another page asks anew.
type TextureFeed = {
  media: SlotMedia & { filename: FileName; guid: StringGUID }
  page_number: PageNumber
  print_layer: PrintLayer
}

// `free` deliberately omits `camera_flip`: releasing auto-return keeps the
// user's current orbit instead of snapping the book around.
const CAMERA_SETTINGS: Record<CameraMode, Partial<SettingsValues>> = {
  free: { camera_auto_return: false },
  front: { camera_auto_return: true, camera_flip: false },
}

// The book3d default (Settings.ts) — pdfview's `zoom` (inverse scale, 1 =
// 100%) multiplies it, so zooming the flat plot and the book feel the same.
const BASE_CAMERA_ZOOM = 1.4

export type FrameRange = { end: number; start: number }

// Big, evident circular hit target pinned to the far edges so the book's
// center stays free for the camera's orbit drag. At the first/last frame the
// StepButton is disabled: it stays visible but dimmed (the Button's default
// `disabled:opacity-50`) and, overriding the Button's `disabled:pointer-events-none`
// back to `auto`, keeps swallowing clicks at that edge — so a rage-click on a
// spent arrow does nothing instead of falling through to orbit the camera.
const NAV_ARROW_CLASS =
  "absolute top-1/2 z-10 size-16 -translate-y-1/2 rounded-full bg-background/60 shadow-lg backdrop-blur-sm hover:bg-background/90 disabled:pointer-events-auto"

/**
 * The 3D book preview — the `view_3d` alternative to {@link PlotViewer}. It
 * renders its own {@link ViewerHeader} on top (the host's verbatim actions plus
 * its camera/stripe toggles and its {@link Pager}) and the book below, flanked
 * by the big over-book nav arrows. The pager and the arrows both go through the
 * one `go_to_page` handler, driven by this view's own `frame_range` (frames ≠
 * pages: cover and animation shift it), so there is a single navigation path.
 * The camera preset/animation mode come down as `camera_mode`/`animation`
 * props; the camera preset changes back through `on_camera`, while the
 * stripe/normal toggle is the host's own button, handed down ready-made.
 *
 * The `Viewer` handle is created once per mount; every later change (zoom,
 * frame/page, camera mode, imposition edits) flows through `update_model`, so
 * the scene is never rebuilt for navigation — only a `book` change makes
 * book3d regenerate its meshes internally.
 *
 * Page previews are rasterized on demand: `texture_feed` reactively reads the
 * split pages off `get_file_map` (their geometry sizes the texture) and hands
 * the book the list via `set_textures`; the book itself
 * asks for the rasters of the sheets in its visible window, through the same
 * `request_raster` the flat plot's media use.
 */
export const Book3dViewer = CreateRenderFunction({
  computed: {
    book() {
      return this.result?.book3d()
    },
    // The book's shape, to tell a real change from a recompute: the
    // pass-through re-imposes as each page lands, sizing the same book anew.
    book_key(): string {
      return JSONEncode(this.book ?? null)
    },
    // Every split upload page on its book page, ready to hand over once the
    // viewer exists. The file map reads the store, so this recomputes as the
    // split streams in.
    texture_feed(): TextureFeed[] {
      const result = this.result
      if (!result) return []
      const print_layers = BuildPrintLayers(Keys(result.destinations) as DestinationID[])
      // A destination toggled off in the header loses its textures, as it
      // loses its layer on the flat plot — the same routed `hidden_guides`.
      const shown = new Set(this.active_guides.map(g => g.id))
      const feed: TextureFeed[] = []
      for (const [id, pages] of Items(result.destinations)) {
        const destination = id as DestinationID
        if (!shown.has(destination)) continue
        pages.forEach((page_number, i) => {
          const media = this.request_media(destination as string as FileName, (i + 1) as PageNumber)
          if (!media?.filename || !media.guid) return
          feed.push({
            media: { ...media, filename: media.filename, guid: media.guid },
            page_number,
            print_layer: print_layers[destination]!,
          })
        })
      }
      return feed
    },
    destination_guides(): Guide[] {
      return this.guides.filter(g => g.kind === "destination")
    },
    // Each upload page in the feed by the name the book asks its raster by.
    rasters(): Map<string, SlotMedia["raster"]> {
      return new Map(
        this.texture_feed.map(({ media }) => [
          `${media.page_number}:${media.filename}`,
          media.raster,
        ])
      )
    },
  },
  data: {
    // The viewer's current valid frame range, refreshed by `sync_range` (and
    // reported up for the header pager); the over-book nav arrows read it
    // directly to know when to hide.
    frame_range: undefined as FrameRange | undefined,
    // `book_key` of the last `update_model` push.
    last_book: undefined as string | undefined,
    resize_observer: undefined as ResizeObserver | undefined,
    viewer: undefined as Viewer | undefined,
  },
  methods: {
    // The book asks by upload page, as its textures name it; a page no longer
    // in the feed answers undefined.
    request_raster(filename: FileName, page_number: PageNumber): ReturnType<RequestRaster> {
      return this.rasters.get(`${page_number}:${filename}`)?.() ?? Promise.resolve(undefined)
    },
    // The whole list every time: the book places it and asks for what is in
    // view. Sized against the live page, so it follows a book change too.
    push_textures() {
      const viewer = this.viewer
      if (!viewer) return
      const textures: BookTexture[] = []
      for (const { media, page_number, print_layer } of this.texture_feed) {
        if (!viewer.get_page_size(page_number)) continue
        textures.push({
          page_number,
          texture: {
            ...print_layer,
            filename: media.filename,
            guid: media.guid,
            page: media.page_number,
            // the raster is its trim grown by its bleed
            size:
              media.trim && MatrixBox(media.matrix, AddMargins(ToSpatial(media.trim), media.bleed)),
          } as BookTexture["texture"],
        })
      }
      viewer.set_textures(textures)
    },
    // Pull the valid frame range from the (async-booted) viewer — the pager and
    // arrows read it — and snap the routed frame back inside it, since a cover
    // change or a book/stripe toggle reshapes the range and may leave the
    // current page outside.
    sync_range() {
      const viewer = this.viewer
      if (!viewer) return
      const range = { end: viewer.frame_end(), start: viewer.frame_start() }
      this.frame_range = range
      const clamped = Min(Max(range.start, this.current_page), range.end)
      if (clamped !== this.current_page) this.on_current_page?.(clamped)
    },
    // The one navigation path for both the header pager and the over-book
    // arrows: clamp to the live range.
    go_to_page(n: number) {
      const range = this.frame_range
      this.on_current_page?.(range ? Min(Max(range.start, n), range.end) : n)
    },
    update(changes: Partial<SettingsValues>) {
      this.viewer?.update_model(changes)
    },
  },
  mixins: [RootMixin] as const,
  mounted() {
    const container = this.root?.querySelector<HTMLElement>('[data-ref="book"]')
    const book = this.book
    if (!container || !book) return
    this.last_book = this.book_key
    void create_book_viewer(
      container,
      {
        animation: this.animation,
        book,
        camera_zoom: BASE_CAMERA_ZOOM * (this.zoom || 1),
        frame_selected: this.current_page,
        ...CAMERA_SETTINGS[this.camera_mode],
      },
      this.request_raster
    ).then(viewer => {
      this.viewer = viewer
      this.resize_observer = new ResizeObserver(() => viewer.resize())
      this.resize_observer.observe(container)
      this.sync_range()
      this.push_textures()
    })
  },
  props: {
    // The flat plot's guides, routed as one `hidden_guides`, so a toggle here
    // holds on the flat plot too. Only the destinations: the book has no marks.
    active_guides: [] as Guide[],
    guides: [] as Guide[],
    hidden_guides: [] as string[],
    on_toggle_guide: undefined as ((id: string) => void) | undefined,
    // The book's camera behavior — "book" (page-turn spread) or "stripe"
    // (flat top-down sheets) — owned by the host and routed, like `camera_mode`.
    animation: "normal" as Animation,
    // The active camera preset, owned and toggled by the host (its free-camera
    // button lives in `layout_controls`); the watcher below pushes it into the
    // scene.
    camera_mode: "front" as CameraMode,
    // The selected frame (page-turn position), same route field as the flat
    // plot's page.
    current_page: 0,
    // Read inside `texture_feed`, so only it follows the split; the book asks
    // its rasters through it too (see `request_raster`).
    request_media: undefined as unknown as RequestMedia,
    // See PdfViewerOptions.show_header.
    show_header: true,
    // The host's verbatim right-side header controls (download).
    header_actions: undefined as ComponentChildren,
    // The host's control for the header's corner over the rail (3D switch).
    header_corner: undefined as ComponentChildren,
    lang: "en",
    // The host's finished layout buttons (free camera, stripe toggle) for the
    // left header group, rendered verbatim (see CreatePdfViewer).
    layout_controls: undefined as ComponentChildren,
    on_current_page: undefined as ((n: number) => void) | undefined,
    on_zoom: undefined as ((zoom: number) => void) | undefined,
    result: undefined as ImposeResult | undefined,
    // The host's rail and open panel (see ViewerSidebar), laid out under the
    // header so opening a panel narrows the document, never the header.
    sidebar: undefined as ComponentChildren,
    zoom: 1,
  },
  render() {
    const pdfview = useTranslationTyped("pdfview")
    const range = this.frame_range
    const min = range?.start ?? 0
    const max = range?.end ?? 0
    const value = Min(Max(min, this.current_page), max)
    // Prev/next arrows flanking the book — the same StepButton as the pager,
    // just a bigger hit target; it disables (and fades) at the first/last frame.
    const arrows = [
      {
        direction: "prev",
        icon: <ChevronLeftIcon className="size-8" />,
        side: "left-6",
        title: pdfview.PREV_PAGE(),
      },
      {
        direction: "next",
        icon: <ChevronRightIcon className="size-8" />,
        side: "right-6",
        title: pdfview.NEXT_PAGE(),
      },
    ] as const
    return (
      <div className="flex h-full w-full flex-col">
        {this.show_header && (
          <ViewerHeader
            center={
              <>
                <Pager max={max} min={min} on_value={this.go_to_page} value={value} />
                <Separator className="data-[orientation=vertical]:h-4" orientation="vertical" />
                <ZoomControl on_zoom={n => this.on_zoom?.(n)} zoom={this.zoom} />
              </>
            }
            left={
              <>
                <GuideToggles
                  guides={this.destination_guides}
                  hidden={this.hidden_guides}
                  on_toggle={id => this.on_toggle_guide?.(id)}
                />
                {this.layout_controls}
              </>
            }
            corner={this.header_corner}
            right={this.header_actions}
          />
        )}
        <div className="flex min-h-0 flex-1">
          {this.sidebar}
          <div className="relative min-w-0 flex-1 bg-content">
            <div className="h-full w-full" data-ref="book" />
            {/* A single-frame book (a one-sheet flyer, or a stripe view with
                nothing to step through) has nowhere to go: both arrows would be
                permanently disabled, so drop them entirely rather than park two
                dimmed circles over the model. `max > min` also covers the boot
                window, where `frame_range` is still undefined. */}
            {max > min &&
              arrows.map(({ direction, icon, side, title }) => (
                <StepButton
                  className={`${NAV_ARROW_CLASS} ${side}`}
                  direction={direction}
                  key={side}
                  max={max}
                  min={min}
                  on_value={this.go_to_page}
                  size="icon"
                  title={title}
                  value={value}
                  variant="outline"
                >
                  {icon}
                </StepButton>
              ))}
          </div>
        </div>
      </div>
    )
  },
  unmounted() {
    this.resize_observer?.disconnect()
    this.viewer?.dispose()
  },
  watch: {
    // `frame_start`/`frame_end` depend on the animation mode (stripe reads
    // every sheet from 0, book counts openings) — resync the range after the
    // mode change, same as a book/cover change.
    animation(next: Animation) {
      this.update({ animation: next })
      this.sync_range()
    },
    camera_mode(mode: CameraMode) {
      this.update(CAMERA_SETTINGS[mode])
    },
    current_page(n: number) {
      this.update({ frame_selected: n })
    },
    // Imposition edits reshape the book — push the new model (book3d rebuilds
    // its meshes) and re-feed the textures, sized to the new pages. Guarded on
    // the book's shape so a recompute that changes nothing never rebuilds.
    book_key(key: string) {
      const book = this.book
      if (!book || !this.viewer || key === this.last_book) return
      this.last_book = key
      this.update({ book })
      this.sync_range()
      this.push_textures()
    },
    texture_feed() {
      this.push_textures()
    },
    zoom() {
      this.update({ camera_zoom: BASE_CAMERA_ZOOM * (this.zoom || 1) })
    },
  },
})
